import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, shareReplay } from 'rxjs/operators';

export interface SessionConfig {
  oAuthLogoutUrl: string;
  oAuthRedirectUrl: string;
  hydraFrontendServer: string;
  oAuthAppSubDomain: string;
  requireHttps: string;
  hydraClient: string;
  loginChallengeExpiryDuration: string;
  accessId: string;
  staticResourcePath: string;
}

@Injectable({
  providedIn: 'root'
})
export class SessionConfigService {
  private config$ = new BehaviorSubject<SessionConfig | null>(null);
  private configCache$: Observable<SessionConfig> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Loads configuration from the server.
   * Uses caching to prevent multiple API calls.
   */
  loadConfig(): Observable<SessionConfig> {
    if (this.configCache$) {
      return this.configCache$;
    }

    this.configCache$ = this.http.get<SessionConfig>('/uas/v1/session-config').pipe(
      tap(config => {
        this.config$.next(config);
        // Store in sessionStorage for backward compatibility
        this.storeInSessionStorage(config);
      }),
      catchError(error => {
        console.error('Failed to load session config:', error);
        // Return default/fallback config
        const defaultConfig = this.getDefaultConfig();
        this.config$.next(defaultConfig);
        this.storeInSessionStorage(defaultConfig);
        return of(defaultConfig);
      }),
      shareReplay(1) // Cache the result
    );

    return this.configCache$;
  }

  /**
   * Get the current configuration (synchronous).
   * Returns null if config hasn't been loaded yet.
   */
  getConfig(): SessionConfig | null {
    return this.config$.value;
  }

  /**
   * Get configuration as an observable.
   */
  getConfig$(): Observable<SessionConfig | null> {
    return this.config$.asObservable();
  }

  /**
   * Get a specific config value.
   */
  getValue(key: keyof SessionConfig): string | null {
    const config = this.config$.value;
    return config ? config[key] : null;
  }

  /**
   * Store config in sessionStorage for backward compatibility.
   * This allows existing code using sessionStorage.getItem() to continue working.
   */
  private storeInSessionStorage(config: SessionConfig): void {
    Object.keys(config).forEach(key => {
      sessionStorage.setItem(key, config[key as keyof SessionConfig]);
    });
  }

  /**
   * Default/fallback configuration.
   */
  private getDefaultConfig(): SessionConfig {
    return {
      oAuthLogoutUrl: '',
      oAuthRedirectUrl: '',
      hydraFrontendServer: '',
      oAuthAppSubDomain: '',
      requireHttps: 'false',
      hydraClient: '',
      loginChallengeExpiryDuration: '300',
      accessId: '',
      staticResourcePath: ''
    };
  }

  /**
   * Clear cached configuration (useful for testing or logout).
   */
  clearConfig(): void {
    this.config$.next(null);
    this.configCache$ = null;
  }
}
